import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import { DEFAULT_SETTINGS, type AppSettings } from '@/core/types/settings'
import { getDeviceId } from './syncStateRepository'
import { logger } from '@/core/logging/logger'

/**
 * Settings live in a single row holding one JSON blob, so adding a setting
 * needs no migration. See 02-data-model.md section 8.
 *
 * The blob is merged over DEFAULT_SETTINGS on read, which means a row written
 * by an older version of the app -- or by the other device running an older
 * version -- gains new fields automatically instead of returning undefined.
 */

export const SETTINGS_ID = 'singleton'

export async function getSettings(db: BlockyTimeDB = defaultDb): Promise<AppSettings> {
  const row = await db.settings.get(SETTINGS_ID)
  if (!row || row.deletedAt !== null) return { ...DEFAULT_SETTINGS }

  try {
    const parsed = JSON.parse(row.data) as Partial<AppSettings>
    return { ...DEFAULT_SETTINGS, ...parsed }
  } catch (err) {
    // A corrupt blob must not brick the app. Fall back to defaults loudly.
    logger.error('db', 'Settings JSON is unparseable; using defaults', err)
    return { ...DEFAULT_SETTINGS }
  }
}

/**
 * Writes a partial update. Always sets updatedAt, deviceId and _dirty --
 * miss any one and the row silently never syncs. This is exactly why writes
 * go through repositories and never through raw Dexie calls in components.
 */
export async function updateSettings(
  patch: Partial<AppSettings>,
  db: BlockyTimeDB = defaultDb,
): Promise<AppSettings> {
  const deviceId = await getDeviceId(db)
  const current = await getSettings(db)
  const next: AppSettings = { ...current, ...patch }

  await db.settings.put({
    id: SETTINGS_ID,
    data: JSON.stringify(next),
    updatedAt: Date.now(),
    deletedAt: null,
    deviceId,
    serverSeq: 0,
    _dirty: 1,
  })

  return next
}

/** Creates the settings row on first launch if it is missing. */
export async function ensureSettings(db: BlockyTimeDB = defaultDb): Promise<AppSettings> {
  const row = await db.settings.get(SETTINGS_ID)
  if (row && row.deletedAt === null) return getSettings(db)

  logger.info('db', 'Creating settings row with defaults')
  return updateSettings({}, db)
}
